import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";

// Ürünün entegrasyon (kanal) durumları. Satır içi dropdown'a sığmıyordu: çok şubeli bir üründe
// şube grupları + kanal switch'leri + toplu düğmeler 300px içinde eziliyordu.
//
// Sayfa seviyesinde tek örnek olarak duruyor (Products.jsx), satır başına değil — yoksa listedeki
// her ürün için ayrı bir modal oluşurdu.

// Toplu düğmeler yalnızca değiştirecek bir şey varken tıklanabilir. Hesap sadece aktif/pasif
// destekleyen kanallar üzerinden: POS satırları zaten değiştirilemiyor, sayılsalar düğme hep
// tıklanabilir görünür ama tıklayınca hiçbir şey olmazdı.
const toggleableOf = (items) => items.filter((item) => item.supportsSetActive);
const allActiveIn = (items) =>
  toggleableOf(items).every((item) => item.mapping.isActive);
const allInactiveIn = (items) =>
  toggleableOf(items).every((item) => !item.mapping.isActive);

// Şubeye göre grupla — çok bağlantıda düz liste karışıyor.
//
// Eşleşmeyen kanallar da listeye giriyor. Eskiden yalnızca eşleşmişler çiziliyordu: fiyat
// gönderiminde "8 kanala gönderildi" deyip 4'ünü atlayınca eksik olanın hangisi olduğu
// hiçbir ekranda görünmüyordu.
const groupByBranch = (integrations) => {
  const groups = [];
  const byBranchId = new Map();

  for (const integration of integrations) {
    let group = byBranchId.get(integration.branchId);

    if (!group) {
      group = {
        branchId: integration.branchId,
        branchName: integration.branchName,
        items: [],
      };
      byBranchId.set(integration.branchId, group);
      groups.push(group);
    }

    group.items.push(integration);
  }

  return groups.map((group) => {
    const matched = group.items.filter((item) => item.mapping?.remoteId);

    return {
      ...group,
      matched,
      unmatched: group.items.filter((item) => !item.mapping?.remoteId),
      toggleable: matched.some((item) => item.supportsSetActive),
      allActive: allActiveIn(matched),
      allInactive: allInactiveIn(matched),
    };
  });
};

export function ProductIntegrationsModal({
  opened,
  onClose,
  product,
  integrations = [],
  // Eşleştirmeler bağlantı başına ayrı sorgudan geliyor ve geç yerleşiyor. Bayrak olmadan boş
  // dizi "eşleşme yok" gibi okunuyor ve başlıkta "0 eşleşmeden 0 tanesi satışta" yazıyordu —
  // eksik değil, YANLIŞ bir cümle.
  loading = false,
  pending = {},
  onToggleMapping,
  onToggleAllMappings,
  onToggleBranchMappings,
}) {
  const { can } = useAccess();
  const canToggle = can("connections");

  // Kanal isteği uçarken pencere kapanabilirse kullanıcı işlemi iptal ettiğini sanıyor: istek
  // sürüyor, sonucu ve hatayı hiç görmüyor. POS rölesinde bu 10 sn'yi buluyor.
  const busy =
    pending.mappingConnectionId != null ||
    pending.branchMappings != null ||
    pending.allMappings != null;
  const closeIfIdle = () => {
    if (!busy) {
      onClose();
    }
  };

  if (!product) {
    return null;
  }

  const matched = integrations.filter((item) => item.mapping?.remoteId);
  const unmatchedCount = integrations.length - matched.length;
  const groups = groupByBranch(integrations);
  // Kanalda aktif/pasif değiştirmek API'de connections:write istiyor. Yetkisi olmayan durumu
  // görür ama değiştiremez — toplu düğmeler ve switch'ler kilitlenir, gizlenmez.
  const anySetActive = matched.some((item) => item.supportsSetActive);
  const activeCount = matched.filter((item) => item.mapping.isActive).length;

  return (
    <Modal
      opened={opened}
      onClose={closeIfIdle}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      closeButtonProps={{
        disabled: busy,
        title: busy ? "İşlem sürüyor" : undefined,
      }}
      centered
      radius="md"
      size="lg"
      title={
        <div>
          <Text fw={600}>Entegrasyon Durumu</Text>
          <Text size="xs" c="dimmed">
            {product.name}
          </Text>
        </div>
      }
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {loading
              ? "Eşleştirmeler yükleniyor…"
              : `${matched.length} eşleşmeden ${activeCount} tanesi satışta${unmatchedCount ? ` · ${unmatchedCount} kanalda eşleşme yok` : ""}`}
          </Text>
          {!loading && anySetActive && (
            <Group gap="xs">
              <Button
                size="compact-sm"
                variant="light"
                color="teal"
                {...lockProps(canToggle, { disabled: allActiveIn(matched) })}
                loading={pending.allMappings === true}
                onClick={() => onToggleAllMappings(product, true)}
              >
                Tümünü aç
              </Button>
              <Button
                size="compact-sm"
                variant="light"
                color="gray"
                {...lockProps(canToggle, { disabled: allInactiveIn(matched) })}
                loading={pending.allMappings === false}
                onClick={() => onToggleAllMappings(product, false)}
              >
                Tümünü kapat
              </Button>
            </Group>
          )}
        </Group>

        {/* Bir kısmı gelmiş olabilir: gelen şube kartları görünür, altında yükleniyor
                    göstergesi kalır. Boş bir modal göstermektense kısmi liste daha bilgilendirici. */}
        {loading && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}

        {groups.map((group) => (
          <Card key={group.branchId} withBorder radius="md" p="sm">
            <Group justify="space-between" wrap="nowrap" mb="xs">
              <Text fw={600} size="sm">
                {group.branchName}
              </Text>
              {group.toggleable && (
                <Group gap={4} wrap="nowrap">
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="teal"
                    {...lockProps(canToggle, { disabled: group.allActive })}
                    loading={
                      pending.branchMappings?.branchId === group.branchId &&
                      pending.branchMappings.isActive === true
                    }
                    onClick={() =>
                      onToggleBranchMappings(
                        product,
                        group.branchId,
                        group.branchName,
                        true,
                      )
                    }
                  >
                    Aç
                  </Button>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    {...lockProps(canToggle, { disabled: group.allInactive })}
                    loading={
                      pending.branchMappings?.branchId === group.branchId &&
                      pending.branchMappings.isActive === false
                    }
                    onClick={() =>
                      onToggleBranchMappings(
                        product,
                        group.branchId,
                        group.branchName,
                        false,
                      )
                    }
                  >
                    Kapat
                  </Button>
                </Group>
              )}
            </Group>

            <Stack gap="xs">
              {group.matched.map((integration) => (
                <Group
                  key={integration.connectionId}
                  justify="space-between"
                  wrap="nowrap"
                >
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text size="sm" truncate>
                      {integration.channelName}
                    </Text>
                    {!integration.supportsSetActive && (
                      <Badge size="xs" variant="light" color="gray">
                        salt okunur
                      </Badge>
                    )}
                  </Group>
                  {/* İstek kanalın kendi API'sine gidiyor (POS relay'de 10 sn'ye
                                        kadar sürebiliyor) — switch'in yerini spinner alır. */}
                  {pending.mappingConnectionId === integration.connectionId ? (
                    <Loader size="xs" color="teal" />
                  ) : (
                    <Switch
                      aria-label={`${integration.channelName} entegrasyonunda aktif`}
                      checked={integration.mapping.isActive}
                      color="teal"
                      size="sm"
                      disabled={!integration.supportsSetActive || !canToggle}
                      title={
                        integration.supportsSetActive
                          ? undefined
                          : "Bu entegrasyonda aktif/pasif değiştirilemez"
                      }
                      onChange={(event) =>
                        onToggleMapping(
                          product,
                          integration,
                          event.currentTarget.checked,
                        )
                      }
                    />
                  )}
                </Group>
              ))}

              {/* Eşleşmeyenler switch alamaz (kanalda karşılığı yok) ama listede
                                DURURLAR: fiyat gönderiminde atlanan kanalın hangisi olduğu
                                yoksa hiçbir ekranda görünmüyordu. */}
              {group.unmatched.map((integration) => (
                <Group
                  key={integration.connectionId}
                  justify="space-between"
                  wrap="nowrap"
                >
                  <Text size="sm" c="dimmed" truncate>
                    {integration.channelName}
                  </Text>
                  <Badge size="xs" variant="light" color="orange">
                    eşleşme yok
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Modal>
  );
}
